import API_ENDPOINTS from './apiConfig';
import { getToken } from './auth';

interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
  error?: string;
}

interface SubscriptionsResponse {
  jobalerts: Array<{
    _id: string;
    id: number;
    email: string;
    frequency: string;
    unsubscribed: boolean;
    secondary_category_id: number | null;
    location_is_remote: boolean;
    location_type: string | null;
    jobseeker_id: number | null;
    created_at: string;
    updated_at: string;
    __v: number;
  }>;
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
    hasNextPage: boolean;
    hasPrevPage: boolean;
  };
}

interface JobseekersResponse {
  jobseekers: Array<{
    _id: string;
    uid: string;
    __v: number;
    address: string | null;
    city: string;
    company_name: string;
    country: string;
    created_at: string;
    custom_fields: Record<string, unknown>;
    default_cover_letter: string | null;
    email: string;
    first_name: string;
    full_address: string;
    google_user_id: string | null;
    is_approved: boolean;
    is_current_role: boolean;
    is_featured: boolean;
    is_public: boolean;
    is_sample: boolean;
    last_indexed_at: string;
    last_name: string;
    linkedin_url: string | null;
    linkedin_user_id: string | null;
    open_to_work: boolean;
    password: string;
    phone_number: string | null;
    photo_img_url: string | null;
    postal_code: string | null;
    remote_ok: boolean;
    resume_text: string | null;
    resume_url: string | null;
    sso_profile_id: string | null;
    sso_raw_attributes: string | null;
    state: string;
    stripe_customer_id: string | null;
    summary: string | null;
    title: string;
    updated_at: string;
    website_url: string | null;
  }>;
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
    hasNextPage: boolean;
    hasPrevPage: boolean;
  };
}

interface ApplicantsWithJobseekerResponse {
  applications: Array<{
    _id: string;
    id: number;
    ip: string;
    type: string;
    created_at: string;
    job_seeker_id: number;
    job: {
      id: number;
      uid: string;
      title: string;
      slug: string;
      experience: string | null;
      location_name: string;
      apply_by_form: boolean;
      apply_url: string | null;
      apply_email: string | null;
      description_html: string;
      is_remote: boolean;
      remote_only: boolean;
      remote_required_location: string | null;
      is_featured: boolean;
      is_paid: boolean;
      is_published: boolean;
      is_draft: boolean;
      published_at: string;
      scheduled_at: string | null;
      expires_on: string;
      is_filled: boolean;
      filled_at: string | null;
      salary_min: string;
      salary_max: string;
      salary_currency: string;
      salary_timeframe: string;
      salary_description: string | null;
      show_salary: boolean;
      how_to_apply: string | null;
      is_sample: boolean;
      custom_fields: Record<string, unknown>;
      last_indexed_at: string;
      last_google_indexed_at: string | null;
      last_google_deindexed_at: string | null;
      created_at: string;
      updated_at: string;
      jobboard_id: number;
      company_id: number;
      jobtype_id: number;
      category_id: number;
      secondary_category_id: number;
      location_id: number;
    };
    updated_at: string;
    __v: number;
    jobseeker: {
      _id: string;
      uid: string;
      __v: number;
      address: string | null;
      city: string;
      company_name: string;
      country: string;
      created_at: string;
      custom_fields: Record<string, unknown>;
      default_cover_letter: string | null;
      email: string;
      first_name: string;
      full_address: string;
      google_user_id: string | null;
      is_approved: boolean;
      is_current_role: boolean;
      is_featured: boolean;
      is_public: boolean;
      is_sample: boolean;
      last_indexed_at: string;
      last_name: string;
      linkedin_url: string | null;
      linkedin_user_id: string | null;
      open_to_work: boolean;
      password: string;
      phone_number: string | null;
      photo_img_url: string | null;
      postal_code: string | null;
      remote_ok: boolean;
      resume_text: string | null;
      resume_url: string | null;
      sso_profile_id: string | null;
      sso_raw_attributes: string | null;
      state: string;
      stripe_customer_id: string | null;
      summary: string | null;
      title: string;
      updated_at: string;
      website_url: string | null;
      id: number;
      full_name: string;
      location: string;
    };
  }>;
  summary: {
    totalWithJobSeeker: number;
    totalAllApplications: number;
    percentageWithJobSeeker: number;
  };
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
    hasNextPage: boolean;
    hasPrevPage: boolean;
  };
}

interface JobObject {
  id: string | number;
  name: string;
  slug: string;
  secondary_lang_name?: string;
  [key: string]: unknown;
}

interface JobsResponse {
  jobs: Array<{
    _id: string;
    title: string;
    company: string | JobObject;
    location: string | JobObject;
    location_name?: string;
    is_published: boolean;
    is_filled: boolean;
    is_remote: boolean;
    category: string | JobObject;
    jobtype: string | JobObject;
    salary_min?: number;
    salary_max?: number;
    salary_timeframe?: string;
    created_at: string;
    updated_at: string;
    description?: string;
    requirements?: string;
    benefits?: string;
    contact_email?: string;
    contact_phone?: string;
    application_url?: string;
  }>;
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
    hasNextPage: boolean;
    hasPrevPage: boolean;
  };
}

interface CompanyResponse {
  company: {
    id: number;
    name: string;
    slug: string;
    email: string;
    logo_url: string;
    site_url: string | null;
    twitter_handle: string | null;
    linkedin_url: string | null;
    facebook_url: string | null;
    tagline: string;
    description_html: string | null;
    anonymity_enabled: boolean;
    is_verified: boolean;
    is_approved: boolean;
    is_published: boolean;
    contact_name: string;
    is_member: boolean;
    created_at: string;
    updated_at: string;
  };
}

interface TeamMember {
  id: number;
  name: string;
  slug: string;
  email: string;
  logo_url: string;
  site_url: string | null;
  tagline: string | null;
  contact_name: string;
  is_verified: boolean;
  is_approved: boolean;
  is_published: boolean;
  is_member: boolean;
  created_at: string;
  updated_at: string;
}

interface TeamMembersResponse {
  teamMembers: TeamMember[];
  pagination: {
    total: number;
    limit: number;
    offset: number;
    hasMore: boolean;
  };
  filters: {
    companyId: number;
    sort: string;
    order: string;
  };
}


export const makeAuthenticatedRequest = async <T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<ApiResponse<T>> => {
  const token = getToken();

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string> || {}),
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(endpoint, {
    ...options,
    headers,
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return response.json();
};

export const fetchSubscriptions = async (params: {
  page?: number;
  limit?: number;
  frequency?: string;
  locationIsRemote?: boolean;
  email?: string;
  unsubscribed?: boolean;
  sortBy?: string;
  sortOrder?: string;
} = {}) => {
  const searchParams = new URLSearchParams();
  
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      searchParams.append(key, value.toString());
    }
  });

  const url = `${API_ENDPOINTS.subscriptions}${searchParams.toString() ? `?${searchParams.toString()}` : ''}`;
  
  return makeAuthenticatedRequest<SubscriptionsResponse>(url);
};

export const fetchJobseekers = async (params: {
  page?: number;
  limit?: number;
  email?: string;
  firstName?: string;
  lastName?: string;
  location?: string;
  isActive?: boolean;
  sortBy?: string;
  sortOrder?: string;
} = {}) => {
  const searchParams = new URLSearchParams();
  
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      searchParams.append(key, value.toString());
    }
  });

  const url = `${API_ENDPOINTS.jobseekers}${searchParams.toString() ? `?${searchParams.toString()}` : ''}`;
  
  return makeAuthenticatedRequest<JobseekersResponse>(url);
};

export const fetchApplicants = async (params: {
  page?: number;
  limit?: number;
  email?: string;
  firstName?: string;
  lastName?: string;
  location?: string;
  jobTitle?: string;
  sortBy?: string;
  sortOrder?: string;
} = {}) => {
  const searchParams = new URLSearchParams();
  
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      searchParams.append(key, value.toString());
    }
  });

  const url = `${API_ENDPOINTS.applicants}${searchParams.toString() ? `?${searchParams.toString()}` : ''}`;
  
  return makeAuthenticatedRequest<ApplicantsWithJobseekerResponse>(url);
};

export const updateHiredStatus = async (applicationId: number, isHired: boolean) => {
  const url = API_ENDPOINTS.hiredStatus;
  
  return makeAuthenticatedRequest<{ success: boolean; message: string }>(url, {
    method: 'POST',
    body: JSON.stringify({
      application_id: applicationId,
      hired: isHired
    })
  });
};

export const fetchJobs = async (params: {
  page?: number;
  limit?: number;
  title?: string;
  location?: string;
  company?: string;
  is_published?: boolean;
  is_filled?: boolean;
  is_remote?: boolean;
  category?: string;
  jobtype?: string;
  salary_min?: number;
  salary_max?: number;
  sortBy?: string;
  sortOrder?: string;
} = {}) => {
  const searchParams = new URLSearchParams();
  
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      searchParams.append(key, value.toString());
    }
  });

  const url = `${API_ENDPOINTS.jobs}${searchParams.toString() ? `?${searchParams.toString()}` : ''}`;
  
  return makeAuthenticatedRequest<JobsResponse>(url);
};



export const fetchMetrics = async (startDate: string, endDate: string, compareStartDate?: string, compareEndDate?: string) => {
  const searchParams = new URLSearchParams();
  searchParams.append('start', startDate);
  searchParams.append('end', endDate);
  
  if (compareStartDate && compareEndDate) {
    searchParams.append('compareStart', compareStartDate);
    searchParams.append('compareEnd', compareEndDate);
  }
  
  const url = `${API_ENDPOINTS.metrics}?${searchParams.toString()}`;
  return makeAuthenticatedRequest(url);
};

export const fetchCompany = async () => {
  return makeAuthenticatedRequest<CompanyResponse>(API_ENDPOINTS.company);
};

export type CompanyNotificationSubscribe = {
  quarterly_email?: boolean;
  team?: boolean;
  marketing?: boolean;
  updates?: boolean;
};

interface CompanyNotificationsResponse {
  subscribe: {
    quarterly_email: boolean;
    team: boolean;
    marketing: boolean;
    updates: boolean;
  };
}

export const fetchCompanyNotifications = async (): Promise<
  ApiResponse<CompanyNotificationsResponse>
> => {
  return makeAuthenticatedRequest<CompanyNotificationsResponse>(
    API_ENDPOINTS.companyNotifications
  );
};

export const updateCompanyNotifications = async (
  subscribe: CompanyNotificationSubscribe
): Promise<ApiResponse<CompanyNotificationsResponse>> => {
  return makeAuthenticatedRequest<CompanyNotificationsResponse>(API_ENDPOINTS.companyNotifications, {
    method: 'PUT',
    body: JSON.stringify({ subscribe }),
  });
};

export const fetchTeamMembers = async (params: {
  page?: number;
  limit?: number;
  is_active?: boolean;
  sortBy?: string;
  sortOrder?: string;
} = {}) => {
  const searchParams = new URLSearchParams();
  
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      searchParams.append(key, value.toString());
    }
  });

  const url = `${API_ENDPOINTS.teamMembers}${searchParams.toString() ? `?${searchParams.toString()}` : ''}`;
  
  return makeAuthenticatedRequest<TeamMembersResponse>(url);
}; 